import { useNavigate } from "react-router-dom";
import OptimizedImage from "@/components/base/OptimizedImage";

export default function Hero() {
  const navigate = useNavigate();

  const scrollDown = () => {
    const el = document.querySelector("#what-is-sdav");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section className="pt-16 bg-white">
      {/* Top bar */}
      <div className="bg-navy py-2.5 px-4 text-center">
        <p className="font-body text-xs text-white/70">
          <span className="text-orange font-600 mr-2">CUBE MC</span>
          A CUBEgroup Company — Software-Defined AV specialists.
        </p>
      </div>

      {/* Hero layout */}
      <div className="max-w-7xl mx-auto px-5 md:px-10 pt-16 md:pt-20 pb-0">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">

          {/* Left: Copy */}
          <div>
            {/* Overline */}
            <p className="section-label block mb-3">
              Software-Defined AV
            </p>

            {/* Headline — visible h1, no entrance animations */}
            <h1 className="font-headline font-500 text-fluid-display text-navy leading-[1.1] mb-4 min-h-[2.4em] flex flex-col justify-start">
              <span>
                Don{"\u2019"}t Just Play Content.{" "}
                <span className="text-orange inline-block relative">
                  Drive Behaviour.
                </span>
              </span>
            </h1>

            {/* Paragraphs */}
            <p
              data-speakable="hero-description"
              className="font-body text-base text-neutral-500 leading-[1.65] mb-4 max-w-lg w-full"
            >
              Your AV installation exists for one reason: To Influence What People Do.
            </p>
            <p
              data-speakable="value-prop"
              className="font-body text-base font-400 text-neutral-500 leading-[1.65] mb-4 max-w-lg w-full"
            >
              Every environment you manage has a behavioral job to do — shape attention, extend dwell time, prompt a decision, reduce anxiety, sell. Legacy AV systems lock you into a static design the moment installation is complete. CUBE MC gives you live, agile control over how your environments perform. Redesign any zone, trigger any cue, reconfigure any site — instantly and remotely, across your entire business.
            </p>

            {/* CTAs */}
            <div className="flex flex-col gap-3 mb-10 sm:flex-row">
              <button onClick={() => navigate("/request-a-call")} className="btn-primary text-base px-7 w-full sm:w-auto">
                Request a Call
              </button>
              <button onClick={scrollDown} className="btn-secondary text-base px-7 w-full sm:w-auto">
                What is SDAV?
              </button>
            </div>

            {/* Mini trust row */}
            <div className="flex flex-wrap items-center gap-5 pt-5 border-t border-neutral-100">
              {[
                { icon: "ri-wifi-off-line",      text: "Network-Resilient",    color: "text-platform" },
                { icon: "ri-global-line",        text: "20,000+ Environments Managed", color: "text-platform" },
              ].map((t) => (
                <div key={t.text} className="flex items-center gap-1.5">
                  <i className={`${t.icon} ${t.color} text-base`} />
                  <span className="font-body text-xs text-neutral-500">{t.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Image + stat */}
          <div className="relative">
            {/* Image — no entrance animations */}
            <div className="relative rounded-lg overflow-hidden border border-neutral-200">
              <div className="w-full h-64 md:h-80 lg:h-[420px]">
                <OptimizedImage
                  seqId="hero_opt_01"
                  alt="Commercial lobby with CUBE MC Software-Defined AV installation — ceiling speakers, integrated screens and zone control"
                  layout="halfGrid"
                  aboveFold={true}
                  fit="cover"
                  position="object-top"
                />
              </div>
            </div>
            {/* Floating stat */}
            <div className="absolute -top-4 -right-3 md:-right-6 bg-platform text-white rounded-lg px-4 py-3 hidden md:block">
              <div className="font-headline font-500 text-2xl leading-none">&lt;90</div>
              <div className="font-body text-xs font-500 mt-0.5 text-white/85">Days to ROI</div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll nudge */}
      <div className="max-w-7xl mx-auto px-5 md:px-10 py-8 flex items-center justify-center">
        <button
          onClick={scrollDown}
          className="flex flex-col items-center gap-1.5 text-neutral-300 hover:text-orange transition-colors cursor-pointer group"
          aria-label="Scroll to learn more"
        >
          <span className="font-body text-xs tracking-wide">What is Software-Defined AV?</span>
          <i className="ri-arrow-down-line text-lg group-hover:translate-y-1 transition-transform" />
        </button>
      </div>
    </section>
  );
}